import React from 'react';
import { Settings, Database, Wifi } from 'lucide-react';
import { useEmailAuth } from '../../hooks/useEmailAuth';
import TMDBSettings from '../settings/TMDBSettings';
import TMDBCacheSettings from '../settings/TMDBCacheSettings';
import SupabaseSettings from '../settings/SupabaseSettings';
import UserManagement from '../settings/UserManagement';
import AdminPreferences from '../settings/AdminPreferences';
import CacheManagement from '../settings/CacheManagement';
import ThemeSelector from '../settings/ThemeSelector';
import { runSupabaseConnectivityTest } from '../../utils/supabaseConnectivityTest';
import { runDatabaseSchemaTest } from '../../utils/databaseSchemaTest';
import { runTableStructureTest } from '../../utils/tableStructureTest';
import QueueStatus from '../debug/QueueStatus';

export default function SettingsTab({ 
  isAdmin = false,
  allMovies = [],
  onBatchEnrichMovies = null,
  defaultTab = 'viewer',
  onDefaultTabChange = null,
  countdownImages = [],
  onCountdownImagesChange = null,
  currentTheme = null,
  onThemeChange = null
}) {
  const { isUserAuthenticated } = useEmailAuth();
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gray-800 border-2 border-orange-600 rounded-lg p-6">
        <div className="flex items-center gap-2 mb-2">
          <Settings className="text-orange-400" size={28} />
          <h1 className="text-2xl font-bold text-orange-300">Settings</h1>
        </div>
        <p className="text-orange-200">
          Configure your Calendar of Horror experience
        </p>
        {!isUserAuthenticated && (
          <div className="mt-3 p-3 bg-blue-900 bg-opacity-50 border border-blue-600 rounded-lg">
            <p className="text-blue-200 text-sm">
              💡 <strong>Not signed in:</strong> You can configure basic settings below. Sign in to access additional features and server-side storage.
            </p>
          </div>
        )}
      </div>

      {/* Supabase Configuration */}
      <SupabaseSettings />

      {/* TMDB Configuration */}
      <TMDBSettings />

      {/* TMDB Cache Management */}
      <TMDBCacheSettings />

      {/* Admin Preferences (Authenticated Users) */}
      {isUserAuthenticated && (
        <AdminPreferences
          defaultTab={defaultTab}
          onDefaultTabChange={onDefaultTabChange}
          countdownImages={countdownImages}
          onCountdownImagesChange={onCountdownImagesChange}
        />
      )}

      {/* User Management (Admin Only) */}
      {isAdmin && <UserManagement />}

      {/* Cache Management (Admin Only) */}
      <CacheManagement isAdmin={isAdmin} />

      {/* Supabase Connectivity Test (Admin Only) */}
      {isAdmin && (
        <div className="bg-gray-800 border-2 border-orange-600 rounded-lg p-6">
          <div className="flex items-center gap-2 mb-4">
            <Wifi className="text-orange-400" size={24} />
            <h2 className="text-xl font-bold text-orange-300">Supabase Connectivity Test</h2>
          </div>

          <div className="space-y-4">
            <p className="text-orange-200 text-sm">
              Run comprehensive tests to diagnose Supabase issues. Connectivity tests network operations,
              Schema tests table existence, and Structure tests column layouts.
            </p>

            <div className="bg-blue-900 border border-blue-600 rounded-md p-3">
              <div className="text-blue-300 text-sm">
                <strong>Note:</strong> Check the browser console for detailed test results.
                These tests will help identify connectivity, infrastructure, or database schema issues.
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  console.log('🔍 Starting Supabase connectivity test...');
                  runSupabaseConnectivityTest().then(results => {
                    console.log('📊 Connectivity test completed:', results);
                  }).catch(error => {
                    console.error('❌ Connectivity test failed:', error);
                  });
                }}
                className="bg-blue-600 hover:bg-blue-500 text-white px-4 py-2 rounded-md transition-colors flex items-center gap-2"
              >
                <Wifi size={16} />
                Connectivity Test
              </button>
              
              <button
                onClick={() => {
                  console.log('🔍 Starting database schema test...');
                  runDatabaseSchemaTest().then(results => {
                    console.log('📊 Schema test completed:', results);
                  }).catch(error => {
                    console.error('❌ Schema test failed:', error);
                  });
                }}
                className="bg-purple-600 hover:bg-purple-500 text-white px-4 py-2 rounded-md transition-colors flex items-center gap-2"
              >
                <Database size={16} />
                Schema Test
              </button>
              
              <button
                onClick={() => {
                  console.log('🔍 Starting table structure test...');
                  runTableStructureTest().then(results => {
                    console.log('📊 Structure test completed:', results);
                  }).catch(error => {
                    console.error('❌ Structure test failed:', error);
                  });
                }}
                className="bg-green-600 hover:bg-green-500 text-white px-4 py-2 rounded-md transition-colors flex items-center gap-2"
              >
                <Settings size={16} />
                Structure Test
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Batch Movie Enrichment (Admin Only) */}
      {isAdmin && onBatchEnrichMovies && (
        <div className="bg-gray-800 border-2 border-orange-600 rounded-lg p-6">
          <div className="flex items-center gap-2 mb-4">
            <Database className="text-orange-400" size={24} />
            <h2 className="text-xl font-bold text-orange-300">Movie Data Enhancement</h2>
          </div>

          <div className="space-y-4">
            <p className="text-orange-200 text-sm">
              Enhance all movies in your collection with TMDB data including posters, 
              trailers, cast information, and more.
            </p>

            <div className="bg-yellow-900 border border-yellow-600 rounded-md p-3">
              <div className="text-yellow-300 text-sm">
                <strong>Note:</strong> This process will take some time as it fetches data for 
                each movie individually. Current collection: {allMovies.length} movies.
              </div>
            </div>

            <button
              onClick={onBatchEnrichMovies}
              className="bg-purple-600 hover:bg-purple-500 text-white px-4 py-2 rounded-md transition-colors flex items-center gap-2"
            >
              <Database size={16} />
              Enhance All Movies
            </button>
          </div>
        </div>
      )}

      {/* Theme Selector */}
      <ThemeSelector 
        currentTheme={currentTheme}
        onThemeChange={onThemeChange}
        isAdmin={isAdmin}
      />

      {/* Queue Status Monitor (Admin Only) */}
      <QueueStatus isVisible={isAdmin} />

    </div>
  );
}
